import { ConnectionStatus } from "@workspace/mobile";
import { ViewBooking, ViewBookingFromJSONTyped } from "collins-js-sdk";
import {
  getEnvironmentProps,
  GetEnvironmentPropsResponse
} from "../utils/environment";
import {
  addApiCacheStorage,
  apiCacheStorageType,
  deleteApiCacheStorage,
  getApiCacheRequestStorage,
  getApiCacheStorage
} from "../utils/localStorage/apiCacheStorage";
import { addBooking } from "./Booking/UpdateBooking";
import { dmnIdentity } from "../utils/dmnIdentity";

const { fetch: originalFetch } = window;

type localStorageManagerType = {
  connectionStatus: ConnectionStatus;
};
export const offlineLocalStorageManager = async ({
  connectionStatus
}: localStorageManagerType) => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  // if back online - check store and try saving any outstanding requests first
  await applyOutstandingRequests(connectionStatus, environmentVars);

  // create fetch interceptor
  window.fetch = async (resource: RequestInfo | URL, config?: RequestInit) => {
    const allowed = await checkWhitelist(resource, config, environmentVars);
    // if url not in whitelist don't cache
    if (!allowed || config?.cache === "no-cache") {
      return originalFetch(resource, config);
    }

    const storageObject: apiCacheStorageType = {
      storageKey: config?.method === "GET" ? "get" : "update",
      entered: new Date(),
      resource,
      config
    };

    if (connectionStatus === "internet" || connectionStatus === "unknown") {
      const response = await originalFetch(resource, config);
      const reader = await response.clone().json();

      // success - add to store if get method
      if (config?.method === "GET") {
        addApiCacheStorage({
          ...storageObject,
          data: reader
        });
      }

      return response;
    }

    let response;
    let ok = true;
    // fail - check and get from store if get method
    if (config?.method === "GET") {
      response = (await getApiCacheRequestStorage(storageObject))?.data;
    }

    // fail - add to store if not get method
    if (config?.method !== "GET") {
      ok = false;
      addApiCacheStorage(storageObject);
    }

    const returnResponse = new Response(JSON.stringify(response));
    Object.defineProperty(returnResponse, "ok", { value: ok });

    return returnResponse;
  };
};

const applyOutstandingRequests = async (
  connectionStatus: ConnectionStatus,
  environmentVars: GetEnvironmentPropsResponse | undefined
) => {
  if (connectionStatus !== "internet") {
    return;
  }
  const dataToSave = await getApiCacheStorage("update");
  const token = await dmnIdentity.getAccessToken();

  const promises: (
    | ReturnType<typeof originalFetch>
    | Promise<ViewBooking | undefined>
  )[] = [];
  dataToSave?.forEach(async (request) => {
    // if we don't want to assign an area to a new booking when offline, remove this
    // ********************
    // if request is to add new booking - won't have the booking Id to save area so run it through addBooking instead of continuing below.
    const isNewBooking = checkRequestIsNewBooking(request, environmentVars);
    if (isNewBooking) {
      const body = JSON.parse(`${request.config?.body}`);
      const booking = ViewBookingFromJSONTyped(body, false);

      await addBooking(booking);
      return;
    }
    // ********************

    promises.push(
      originalFetch(request.resource, {
        ...request.config,
        headers: {
          ...request.config?.headers,
          Authorization: `Bearer ${token}`
        }
      })
    );
  });
  await Promise.all(promises);

  deleteApiCacheStorage("update");
};

const checkWhitelist = async (
  resource: RequestInfo | URL,
  config: RequestInit | undefined,
  environmentVars: GetEnvironmentPropsResponse | undefined
): Promise<boolean> => {
  if (config?.method === "GET") {
    // allow cache for all get requests - the response is stored and returned if offline
    return true;
  }

  if (config?.method !== "GET") {
    // found a floor in updates - disabling for now
    return false;
  }

  let allowed = false;
  for (const [key, value] of Object.entries(environmentVars ?? {})) {
    // Only allow trusted domains for updates as recalls will pass the bearer token
    if (
      (key === "COLLINS_API_BASE_URL" ||
        key === "CORE_API_BASE_URL" ||
        key === "AUTH_API_BASE_URL") &&
      typeof value === "string"
    ) {
      if (resource.toString().indexOf(value) > -1) {
        allowed = true;
      }
    }
  }
  return allowed;
};

const checkRequestIsNewBooking = (
  request: apiCacheStorageType,
  environmentVars: GetEnvironmentPropsResponse | undefined
): boolean => {
  if (request.resource !== `${environmentVars?.CORE_API_BASE_URL}/bookings`) {
    return false;
  }

  const body = JSON.parse(`${request.config?.body}`);

  if (body.id) {
    return false;
  }

  if (!body.assigned_areas || body.assigned_areas.length === 0) {
    return false;
  }

  return true;
};
